import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { PostCard } from '@/components/PostCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, Camera, Heart, Calendar, Users, UserPlus, UserCheck, Settings, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function Profile() {
  const { userId } = useParams();
  const { user: currentUser, isAuthenticated, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  
  // Edit profile state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: '',
    bio: '',
    show_real_name: true
  });
  
  // Followers/Following dialogs
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    fetchProfile();
    fetchUserPosts();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`, {
        withCredentials: true
      });
      setProfile(response.data);
      setIsFollowing(response.data.is_following);
      // Set edit form values
      setEditForm({
        display_name: response.data.display_name || '',
        bio: response.data.bio || '',
        show_real_name: response.data.show_real_name !== false
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`, {
        params: { user_id: userId },
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch user posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to follow users');
      return;
    }

    setFollowLoading(true);
    try {
      const response = await axios.post(
        `${API}/users/${userId}/follow`,
        {},
        { withCredentials: true }
      );
      
      setIsFollowing(response.data.following);
      setProfile(prev => ({
        ...prev,
        followers_count: prev.followers_count + (response.data.following ? 1 : -1)
      }));
      
      toast.success(response.data.following ? 'Now following' : 'Unfollowed');
    } catch (error) {
      console.error('Follow error:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleEditProfile = async () => {
    setEditLoading(true);
    try {
      const response = await axios.put(
        `${API}/users/me`,
        editForm,
        { withCredentials: true }
      );
      
      setProfile(prev => ({ ...prev, ...response.data }));
      setShowEditDialog(false);
      toast.success('Profile updated successfully');
      
      // Refresh auth context
      checkAuth();
    } catch (error) {
      console.error('Edit profile error:', error);
      toast.error('Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  const fetchFollowers = async () => {
    setLoadingList(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/followers`);
      setFollowers(response.data);
    } catch (error) {
      console.error('Failed to fetch followers:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchFollowing = async () => {
    setLoadingList(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/following`);
      setFollowing(response.data);
    } catch (error) {
      console.error('Failed to fetch following:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const openFollowers = () => {
    setShowFollowers(true);
    fetchFollowers();
  };

  const openFollowing = () => {
    setShowFollowing(true);
    fetchFollowing();
  };

  const handleLikeUpdate = (postId, liked, count) => {
    setPosts(posts.map(p => 
      p.post_id === postId 
        ? { ...p, is_liked: liked, likes_count: count }
        : p
    ));
  };

  // Get display name based on privacy settings
  const getDisplayName = (user) => {
    if (!user) return 'Unknown';
    if (user.show_real_name === false && user.display_name) {
      return user.display_name;
    }
    return user.name;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="profile-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">User not found</h2>
          <Link to="/feed" className="text-purple-400 hover:underline">
            Go back to feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      {/* Profile Header */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Avatar */}
          <div className="avatar-ring">
            <Avatar className="w-24 h-24 sm:w-32 sm:h-32">
              <AvatarImage src={profile.picture} alt={getDisplayName(profile)} />
              <AvatarFallback className="bg-purple-900 text-white text-3xl">
                {getDisplayName(profile)?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
              <h1 
                className="text-2xl sm:text-3xl font-bold text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {getDisplayName(profile)}
              </h1>
              {isOwnProfile ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="btn-secondary rounded-full"
                  onClick={() => setShowEditDialog(true)}
                  data-testid="edit-profile-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <Button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={`rounded-full px-6 ${
                    isFollowing 
                      ? 'btn-secondary' 
                      : 'btn-primary-gradient'
                  }`}
                  data-testid="follow-btn"
                >
                  {followLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isFollowing ? (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Following
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Follow
                    </>
                  )}
                </Button>
              )}
            </div>

            {profile.bio && (
              <p className="text-gray-300 mb-4">{profile.bio}</p>
            )}

            {/* Stats */}
            <div className="flex items-center justify-center sm:justify-start gap-6 flex-wrap">
              <div className="text-center">
                <div className="flex items-center gap-1 text-white font-semibold">
                  <Camera className="w-4 h-4 text-purple-400" />
                  {profile.posts_count}
                </div>
                <span className="text-xs text-gray-400">Views</span>
              </div>
              
              <button 
                onClick={openFollowers}
                className="text-center hover:opacity-80 transition-opacity"
                data-testid="followers-btn"
              >
                <div className="flex items-center gap-1 text-white font-semibold">
                  <Users className="w-4 h-4 text-blue-400" />
                  {profile.followers_count || 0}
                </div>
                <span className="text-xs text-gray-400">Followers</span>
              </button>
              
              <button 
                onClick={openFollowing}
                className="text-center hover:opacity-80 transition-opacity"
                data-testid="following-btn"
              >
                <div className="flex items-center gap-1 text-white font-semibold">
                  <Users className="w-4 h-4 text-green-400" />
                  {profile.following_count || 0}
                </div>
                <span className="text-xs text-gray-400">Following</span>
              </button>
              
              <div className="text-center">
                <div className="flex items-center gap-1 text-white font-semibold">
                  <Heart className="w-4 h-4 text-pink-400" />
                  {profile.likes_received}
                </div>
                <span className="text-xs text-gray-400">Likes</span>
              </div>
              
              {profile.created_at && (
                <div className="text-center">
                  <div className="flex items-center gap-1 text-white font-semibold">
                    <Calendar className="w-4 h-4 text-orange-400" />
                    {format(new Date(profile.created_at), 'MMM yyyy')}
                  </div>
                  <span className="text-xs text-gray-400">Joined</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Posts Section */}
      <div className="mb-6">
        <h2 
          className="text-xl font-semibold text-white mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Window Views
        </h2>
      </div>

      {posts.length > 0 ? (
        <div className="bento-grid">
          {posts.map((post, index) => (
            <PostCard 
              key={post.post_id} 
              post={post} 
              onLikeUpdate={handleLikeUpdate}
              variant={index % 5 === 0 ? 'tall' : 'default'}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 glass-card rounded-2xl">
          <div className="w-16 h-16 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
            <Camera className="w-8 h-8 text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No views yet</h3>
          <p className="text-gray-400 mb-6">
            {isOwnProfile 
              ? "You haven't shared any window views yet" 
              : "This user hasn't shared any window views yet"}
          </p>
          {isOwnProfile && (
            <Link to="/upload">
              <Button className="btn-primary-gradient rounded-full px-6">
                Share Your First View
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#0F0F16] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Profile</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Display Name */}
            <div className="space-y-2">
              <Label className="text-gray-300">Display Name</Label>
              <Input
                value={editForm.display_name}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                placeholder="Choose a display name"
                className="input-ventana"
                data-testid="display-name-input"
              />
              <p className="text-xs text-gray-500">
                This name will be shown instead of your real name if you choose to hide it
              </p>
            </div>

            {/* Privacy Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
              <div>
                <Label className="text-white">Show Real Name</Label>
                <p className="text-xs text-gray-400 mt-1">
                  {editForm.show_real_name 
                    ? `Your name "${profile.name}" will be visible` 
                    : `You'll appear as "${editForm.display_name || 'Anonymous'}"`}
                </p>
              </div>
              <Switch
                checked={editForm.show_real_name}
                onCheckedChange={(checked) => setEditForm({ ...editForm, show_real_name: checked })}
                data-testid="show-name-toggle"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label className="text-gray-300">Bio</Label>
              <Textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="Tell others about yourself..."
                className="input-ventana min-h-[100px] resize-none"
                maxLength={200}
                data-testid="bio-input"
              />
              <p className="text-xs text-gray-500 text-right">
                {editForm.bio.length}/200
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditProfile}
              disabled={editLoading || (!editForm.show_real_name && !editForm.display_name)}
              className="btn-primary-gradient"
              data-testid="save-profile-btn"
            >
              {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Followers Dialog */}
      <Dialog open={showFollowers} onOpenChange={setShowFollowers}>
        <DialogContent className="bg-[#0F0F16] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Followers</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {loadingList ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : followers.length > 0 ? (
              <div className="space-y-3">
                {followers.map((follower) => (
                  <Link
                    key={follower.user_id}
                    to={`/profile/${follower.user_id}`}
                    onClick={() => setShowFollowers(false)}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={follower.picture} />
                      <AvatarFallback className="bg-purple-900 text-white">
                        {getDisplayName(follower)?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-white font-medium">{getDisplayName(follower)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">No followers yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Following Dialog */}
      <Dialog open={showFollowing} onOpenChange={setShowFollowing}>
        <DialogContent className="bg-[#0F0F16] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Following</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {loadingList ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : following.length > 0 ? (
              <div className="space-y-3">
                {following.map((user) => (
                  <Link
                    key={user.user_id}
                    to={`/profile/${user.user_id}`}
                    onClick={() => setShowFollowing(false)}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-purple-900 text-white">
                        {getDisplayName(user)?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-white font-medium">{getDisplayName(user)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">Not following anyone yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { Profile };
